import React, { useState, useEffect, useRef } from 'react';
import { Box } from '@mui/material';

const DAMP_OCU_BLOQUES = ({ 
  incrementoAncho = 100,
  limiteParrafos = 5,
  containerWidth,
  maxParrafos = 3,
  exercise, isRunning, velocity
}) => {
  const [texto, setTexto] = useState(exercise.content);
  const [lineasPorParrafo, setLineasPorParrafo] = useState(exercise.lineas)
  const [parrafos, setParrafos] = useState<string[][][]>([]);
  const [lineaActual, setLineaActual] = useState(0);
  const [velocidad, setVelocidad] = useState(null)
  const svgRef = useRef<SVGSVGElement>(null);
  const canvasRef = useRef<HTMLCanvasElement | null>(null);

  const calcularTamanoFuente = (anchoContenedor: number): number => {
    const tamanoBase = 16;
    const factorReduccion = Math.max(0.5, Math.min(1, anchoContenedor / 900));
    return Math.max(10, Math.floor(tamanoBase * factorReduccion));
  };

  const dividirPalabra = (palabra: string, anchoMaximo: number, context: CanvasRenderingContext2D): [string, string] => {
    let izquierda = '';
    let derecha = palabra;
    while (derecha.length > 0 && context.measureText(izquierda + derecha[0]).width <= anchoMaximo) {
      izquierda += derecha[0];
      derecha = derecha.slice(1);
    }
    return [izquierda, derecha];
  };

  const dividirTexto = () => {
    if (!svgRef.current) return;

    if (!canvasRef.current) {
      canvasRef.current = document.createElement('canvas');
    }
    const context = canvasRef.current.getContext('2d');
    if (!context) return;

    const tamanoFuente = calcularTamanoFuente(containerWidth || 900);
    context.font = `${tamanoFuente}px Arial`;

    const palabras = texto.split(' ');
    const nuevosParrafos: string[][][] = [];
    let lineaActual: string[] = [];
    let parrafoActual: string[][] = [];
    let anchoBase = 280;
    let i = 0;
    let numPalabras = 0
    while (i < palabras.length && nuevosParrafos.length < maxParrafos) {
      const palabra = palabras[i];
      const anchoActual = anchoMaximo - (incrementoAncho * (limiteParrafos - 1 - nuevosParrafos.length) * 1.5);
      const lineaConPalabra = [...lineaActual, palabra];
      const anchoLineaActual = context.measureText(lineaConPalabra.join(' ')).width;

      if (anchoLineaActual <= anchoActual) {
        lineaActual = lineaConPalabra;
        i++;
      } else {
        if (lineaActual.length > 0) {
          numPalabras += lineaActual.length
          parrafoActual.push(lineaActual);
          lineaActual = [];
        } else {
          const palabraDividida = dividirPalabra(palabra, anchoActual, context);
          parrafoActual.push([palabraDividida[0]]);
          palabras.splice(i + 1, 0, palabraDividida[1]);
          i++;
        }
      }

      if (parrafoActual.length === lineasPorParrafo || i === palabras.length) {
        if (lineaActual.length > 0) {
          parrafoActual.push(lineaActual);
        }
        if (parrafoActual.length > 0) {
          nuevosParrafos.push(parrafoActual);
        }
        parrafoActual = [];
        lineaActual = [];
      }
    }

    let velocidadTemp = ((60 / velocity) * 1000);
    const numFilas = exercise.lineas * maxParrafos
    velocidadTemp = velocidadTemp * (numPalabras / numFilas);

    setVelocidad(velocidadTemp)
    setParrafos(nuevosParrafos);
  };

  useEffect(() => {
    dividirTexto();
  }, [texto, lineasPorParrafo, incrementoAncho, limiteParrafos, containerWidth, maxParrafos]);

  useEffect(() => {
    let intervalo = null
    if (isRunning && velocidad){
      const totalLineas = parrafos.reduce((total, parrafo) => total + parrafo.length, 0);
      intervalo = setInterval(() => {
        setLineaActual((prev) => (prev + 1) % totalLineas);
      }, 1000);
    }
    

    return () => { if (intervalo) clearInterval(intervalo)};
  }, [parrafos, isRunning, velocidad]);

  const tamanoFuente = calcularTamanoFuente(containerWidth || 900);
  const alturaLinea = Math.ceil(tamanoFuente * 1.5);
  const padding = Math.ceil(tamanoFuente * 0.625);
  const anchoMaximo = containerWidth 
    ? containerWidth - (padding * 2)
    : 280 + (incrementoAncho * (limiteParrafos - 1) * 1.5);
  const anchoSVG = containerWidth || anchoMaximo + (padding * 2);
  const alturaMaxima = (maxParrafos * lineasPorParrafo * alturaLinea) + (padding * 2);
  const alturaSVG = Math.max(
    (parrafos.reduce((total, parrafo) => total + parrafo.length, 0) * alturaLinea) + (padding * 2),
    alturaMaxima
  );

  const renderLineasDecorativas = (startX: number, startY: number, ancho: number, altura: number, numLineas: number) => {
    const espacioEntreLineas = ancho / (numLineas + 1);
    return Array.from({ length: numLineas }, (_, i) => (
      <line
        key={i}
        x1={startX + espacioEntreLineas * (i + 1)}
        y1={startY}
        x2={startX + espacioEntreLineas * (i + 1)}
        y2={startY + altura}
        stroke="#000000"
        strokeWidth="1"
      />
    ));
  };

  const renderParrafo = (lineas: string[][], startY: number, anchoParrafo: number, index: number) => {
    const alturaParrafo = lineas.length * alturaLinea;
    const xInicio = (anchoSVG - anchoParrafo) / 2;

    if (!canvasRef.current) {
      canvasRef.current = document.createElement('canvas');
    }
    const context = canvasRef.current.getContext('2d');
    if (!context) return null;

    context.font = `${tamanoFuente}px Arial`;

    return (
      <g key={index}>
        <rect
          x={xInicio}
          y={startY - padding / 2}
          width={anchoParrafo}
          height={alturaParrafo + padding}
          fill="none"
          //stroke="red"
          strokeWidth="2"
        />
        {renderLineasDecorativas(xInicio, startY, anchoParrafo, alturaParrafo, index + 1)}
        {lineas.map((linea, lineaIndex) => {
          const lineaY = startY + (lineaIndex * alturaLinea);
          const lineaGlobalIndex = parrafos.slice(0, index).reduce((total, p) => total + p.length, 0) + lineaIndex;
          const esLineaActual = lineaActual === lineaGlobalIndex;
          
          const espacioExtra = anchoParrafo - linea.reduce((total, palabra) => total + context.measureText(palabra).width, 0);
          const espacioEntrePalabras = espacioExtra / (linea.length - 1);
          
          return (
            <g key={lineaIndex}>
              {esLineaActual && (
                <rect
                  x={xInicio}
                  y={lineaY - 2}
                  width={anchoParrafo}
                  height={alturaLinea}
                  fill="lightblue"
                  opacity="0.5"
                />
              )}
              {linea.map((palabra, palabraIndex) => {
                const xPalabra = xInicio + linea.slice(0, palabraIndex).reduce((total, p) => total + context.measureText(p).width + espacioEntrePalabras, 0);
                const anchoPalabra = context.measureText(palabra).width;
                if (anchoPalabra > anchoParrafo) {
                  const [primeraParte, segundaParte] = dividirPalabra(palabra, anchoParrafo, context);
                  return (
                    <React.Fragment key={palabraIndex}>
                      <text
                        x={xPalabra}
                        y={lineaY + (alturaLinea / 2)}
                        fontSize={tamanoFuente}
                        dominantBaseline="middle"
                      >
                        {primeraParte}
                      </text>
                      <text
                        x={xInicio}
                        y={lineaY + alturaLinea + (alturaLinea / 2)}
                        fontSize={tamanoFuente}
                        dominantBaseline="middle"
                      >
                        {segundaParte}
                      </text>
                    </React.Fragment>
                  );
                }
                return (
                  <text
                    key={palabraIndex}
                    x={xPalabra}
                    y={lineaY + (alturaLinea / 2)}
                    fontSize={tamanoFuente}
                    fill={esLineaActual ? '#000000' : "#999"}
                    dominantBaseline="middle"
                  >
                    {palabra}
                  </text>
                );
              })}
            </g>
          );
        })}
      </g>
    );
  };

  return (
    <Box
      sx={{
        width: "100%",
        height: "100%", // Cambia a 100vh para ocupar toda la altura de la ventana
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        p: 1,
        border: "1px solid #e0e0e0",
        borderRadius: "8px",
        boxShadow: "0 4px 6px rgba(0, 0, 0, 0.1)",
        overflow: "hidden",
        background: "#ffffff",
        
      }}
    >
    <svg
      ref={svgRef}
      width="100%"
      height="100%"
      viewBox={`0 0 ${anchoSVG} ${alturaSVG}`}
      preserveAspectRatio="xMidYMid meet"
    >
      {parrafos.map((parrafo, index) => {
        const startY = padding + (parrafos.slice(0, index).reduce((total, p) => total + p.length, 0) * alturaLinea);
        const anchoParrafo = anchoMaximo - (incrementoAncho * (limiteParrafos - 1 - index) * 1.5);
        return renderParrafo(parrafo, startY, anchoParrafo, index);
      })}
    </svg>
    </Box>
  );
};

export default DAMP_OCU_BLOQUES;

